import dayjs from 'dayjs';

import { registerModal, DialogConfig } from './post-login-modal';

const DIALOG_TYPE = { DAILY: 0, POST_REKYC: 2 } as const;
type DialogTypeValue = (typeof DIALOG_TYPE)[keyof typeof DIALOG_TYPE];

const CARF_FORM_URL = '/account/users/carfForm';

interface CarfApiResponse {
  code: number;
  data?: {
    isCarfDialogNeeded: boolean;
    carfDialogType: DialogTypeValue;
    rekycId?: string | number;
    userId?: string | number;
  };
}

interface GetVirtualIdResponse {
  code: number;
  data: string | null;
  detailMsg: string;
  error_code: string;
  error_message: string;
  msg: string;
}

// Type 0: New notification form - shown once per day
// Type 2: Change notification form - shown once per rekyc event
/* eslint-disable @ok/oklint/no-hardcode-variable */
const DIALOG_CONFIGS: Record<DialogTypeValue, DialogConfig> = {
  [DIALOG_TYPE.DAILY]: {
    title: '新規届出書ご提出のお願い',
    contentHtml: `<p style="font-weight: bold" >OECDが公表した国際基準（CARF：暗号資産等報告枠組み）に基づき、日本の法律（実特法：租税条約等の実施に伴う所得税法、法人税法及び地方税法の特例等に関する法律）が改正されました。</p><p>つきましては、2025年12月31日時点においてOKJとの間で暗号資産等取引をしているお客様は、OKJへ居住地国名等を記載した届出書（新規届出書）のご提出が必要となります。ご提出の期限は2026年12月31日となります。</p>`,
    confirmText: '今すぐ提出する',
    cancelText: '後で',
  },
  [DIALOG_TYPE.POST_REKYC]: {
    title: '異動届出書提出のお願い',
    contentHtml: `<p>既に法律（実特法：租税条約等の実施に伴う所得税法、法人税法及び地方税法の特例等に関する法律）の要請に基づき、新規届出書（又は異動届出書）をOKJにご提出いただいておりますが、登録情報の変更に伴い、新たに異動届出書のご提出が必要となります。</p><p>異動届出書のご提出の期限は、原則として、異動を生じた日から3か月を経過する日です。</p>`,
    confirmText: '今すぐ提出する',
    cancelText: '後で',
  },
};

export const registerCarfModal = (): void => {
  registerModal({
    id: 'carf',
    priority: 0,

    fetch: async () => {
      const ont = window.utils?.ont;
      if (!ont) return null;

      try {
        const carf = (await ont.get('/v3/users/carf/dialog')).data as CarfApiResponse;
        // No dialog pending - skip the extra identity request entirely.
        if (carf.code !== 0 || !carf.data?.isCarfDialogNeeded) return carf;

        // Dialog is pending: fetch the stable uuid for the dedup key.
        // Running it after carf gives the App's token sync a round-trip to land,
        // so the id is keyed to the current user.
        const res = (await ont.get('/v3/users/security/virtualId'))
          .data as GetVirtualIdResponse;
        const userId =
          res?.code === 0 && res.data && res.data !== '0' ? res.data : null;

        // Both requests must yield a usable result; otherwise don't show the dialog.
        if (!userId) return null;
        return { ...carf, data: { ...carf.data, userId } };
      } catch {
        return null;
      }
    },

    process: (payload) => {
      const { code, data } = payload as CarfApiResponse;
      if (code !== 0 || !data?.isCarfDialogNeeded) return null;

      const { carfDialogType, rekycId, userId } = data;

      let anchorValue: string | number | undefined;
      if (carfDialogType === DIALOG_TYPE.DAILY) {
        anchorValue = dayjs().format('YYYY-MM-DD');
      } else if (carfDialogType === DIALOG_TYPE.POST_REKYC) {
        anchorValue = rekycId;
      }
      if (!anchorValue) return null;

      const dialogConfig = DIALOG_CONFIGS[carfDialogType];
      if (!dialogConfig) return null;

      return {
        // Prefix with type so different dialog types never share the same anchor
        anchor: `${carfDialogType}_${anchorValue}`,
        dialogConfig: { ...dialogConfig },
        userId: String(userId),
      };
    },

    onConfirm: () => {
      window.location.assign(CARF_FORM_URL);
    },
  });
};
